import React, { Component } from "react";
import * as THREE from "three";
import { MathUtils} from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { PLYLoader } from "three/examples/jsm/loaders/PLYLoader";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { TransformControls } from "three/examples/jsm/controls/TransformControls";
import { uploadFile } from "../S3Manager";
import { grid } from "../Grid";
import { loadPart } from "../services/part/partService";
import {
  CSS2DRenderer,
  CSS2DObject,
} from "three/examples/jsm/renderers/CSS2DRenderer";
import { Mode } from "./CoordinateDisplay";

import LoadingComponent from "../utils/loader/Loader";

const backgroundColor = new THREE.Color("#FFFFFF");
let mesh = new THREE.Mesh();
const grids = grid();
const renderer = new THREE.WebGLRenderer({ antialias: true });
const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(25, window.innerWidth / window.innerHeight, 0.1, 2000);
const raycaster = new THREE.Raycaster();
const arrowHelper = new THREE.ArrowHelper(
  new THREE.Vector3(0, 0, 1),
  new THREE.Vector3(0, 0, 0),
  30,
  0xffff00,
);
const stlLoader = new STLLoader();
const plyLoader = new PLYLoader();
const controls = new OrbitControls(camera, renderer.domElement);
const material = new THREE.MeshPhongMaterial({
  color: 0xd8d8d8,
  side: THREE.DoubleSide,
  flatShading: true,
  transparent: true,
  opacity: 0.85,
  specular: 0xd8d8d8,
});
const vertMaterial = new THREE.MeshPhongMaterial({
  vertexColors: true,
  side: THREE.DoubleSide,
  flatShading: true,
  transparent: true,
  opacity: 0.85,
  specular: 0xd8d8d8,
});
const transformControls = new TransformControls(camera, renderer.domElement);
let mode = Mode.None;
const labelRenderer = new CSS2DRenderer();
const violationLabelDiv = document.createElement("div");
const violationLabel = new CSS2DObject(violationLabelDiv);
let addEvents = true;

function disposeMesh() {
  transformControls.detach();
  mesh.parent?.remove(mesh);
  mesh.geometry.dispose();
  (mesh.material as THREE.MeshPhongMaterial).dispose();
  renderer.renderLists.dispose();
}

function newMesh(geometry, material) {
  disposeMesh();
  mesh = new THREE.Mesh(geometry, material);
  return mesh;
}

export function getMesh() {
  return mesh;
}

class SceneViewer extends Component<any, any> {

  mount: React.RefObject<HTMLDivElement>;
  
  materialPosition = new THREE.Vector3(0, 0, 0);
  materialRotation = new THREE.Euler(0, 0, 0);
  materialScale = new THREE.Vector3(1, 1, 1);
  snapDir = new THREE.Vector3(0, 0, 1);

  constructor(props) {
    super(props);

    violationLabelDiv.style.display = "none";

    if (addEvents) {
      addEvents = false;
      controls.saveState();

      transformControls.addEventListener(
        "dragging-changed",
        (event: any) => {
          console.log(
            "dragging-changed",
            event.value
          );
      
          controls.enabled = !event.value;
      
          if (!event.value) {
            console.log(
              "CALLING updatePartOrientationValues"
            );
      
            this.updatePartOrientationValues();
          }
        }
      );

      transformControls.addEventListener(
        "change",
        () => {
          console.log(
            "transformControls change"
          );
      
          this.redraw();
        }
      );

      renderer.domElement.addEventListener("mousemove", (event: any) => {
        this.onMouseMove(event);
      });

      renderer.domElement.addEventListener("click", (event: any) => {
        this.onMouseClick(event);
      });

      renderer.domElement.addEventListener("wheel", () => {
        this.redraw();
      }, { passive: true });
    } else {
      controls.reset();
    }

    this.mount = React.createRef();

    this.state = {
      showLoader: true,
    };
  }

  pick(event: MouseEvent): any | null {
    // calculate mouse position in normalized device coordinates
    // (-1 to +1) for both components
    if (event !== undefined) {
      let canvasRect = renderer.domElement.getBoundingClientRect();

      let topOffset = canvasRect.top;
      let leftOffset = canvasRect.left;

      let canvasWidth = canvasRect.width;
      let canvasHeight = canvasRect.height;

      let mouse = new THREE.Vector2();
      mouse.x = ((event.clientX - leftOffset) / canvasWidth) * 2 - 1;
      mouse.y = -((event.clientY - topOffset) / canvasHeight) * 2 + 1;

      // update the picking ray with the camera and mouse position
      raycaster.setFromCamera(mouse, camera!);
      // calculate mesh intersecting the picking ray
      let intersected = raycaster.intersectObject(mesh)[0];
      if (intersected) {
        return {
          object: intersected.object,
          point: intersected.point,
          face: intersected.face!,
        }
      }
    }
    return null;
  }

  updateArror(event: MouseEvent) {
    const object = this.pick(event);
    if (object) {
      arrowHelper.position.set(object.point.x, object.point.y, object.point.z);
      this.snapDir.copy(object.face.normal);
      let quat = new THREE.Quaternion();
      object.object.getWorldQuaternion(quat)
      arrowHelper.setDirection(object.face.normal.applyQuaternion(quat));
      arrowHelper.visible = true;
      this.redraw();
    } else if (arrowHelper.visible) {
      arrowHelper.visible = false;
      this.redraw();
    }
  }

  onMouseMove(event: MouseEvent) {
    if (controls.enabled && event.buttons) {
      this.redraw();    // viewing
    } else if (mode === Mode.Snap) {
      this.updateArror(event);
    }
  }

  onMouseClick(event: MouseEvent) {
    if (mode === Mode.Snap && arrowHelper.visible) {
      let meshQuat = new THREE.Quaternion();
      mesh.getWorldQuaternion(meshQuat);
      let quaternion = new THREE.Quaternion()
      quaternion.setFromUnitVectors(this.snapDir.applyQuaternion(meshQuat), new THREE.Vector3(0, 0, -1))
      mesh.rotation.setFromQuaternion(quaternion.multiply(meshQuat));
      this.snapMesh();
      this.updateArror(event);
    }
  }

  componentDidUpdate(prevProps: Readonly<any>): void {
    if (
      (!prevProps?.modelName ||
      prevProps?.modelName !== this.props.modelName) && this.props.partSourceType
    ) {
      this.setState({ showLoader: true });
      this.renderAnalyzedPart();
    }
    if (this.props.partSourceType !== "source") {
      if (this.props.violationDetails?.violation) {
        violationLabelDiv.className = "analyzeConflictLabel";
        violationLabelDiv.textContent = this.props.violationDetails?.violation;
        violationLabelDiv.style.display = "block";
      }
      else {
        violationLabelDiv.className = "analyzeConflictLabel no-violation";
        violationLabelDiv.textContent = "No Violation"
        violationLabelDiv.style.display = "block";
      }
    } else {
      violationLabelDiv.style.display = "none";
    }
  }

  componentDidMount() {
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0xffffff);

    labelRenderer.setSize(window.innerWidth, window.innerHeight);

    if (this.mount.current) {
      this.mount.current.appendChild(renderer.domElement);
      this.mount.current.appendChild(labelRenderer.domElement);

      renderer.domElement.style.margin = "auto";
      renderer.domElement.style.display = "block";
      labelRenderer.domElement.style.position = "absolute";
      labelRenderer.domElement.style.top = "0px";
      labelRenderer.domElement.style.pointerEvents = "none";
    }

    camera.position.set(0, 0, 400);

    controls.update();

    scene.add(violationLabel);

    this.showGrid();
    // const modelName = this.props.modelName;
    this.renderAnalyzedPart();
    this.redraw();
  }

  renderAnalyzedPart = () => {
    disposeMesh();
    this.disableTransformControls();
    if (this.props.modelName && this.props.modelName !== "") {
      console.log("Part ID: " + this.props.partId + ", Model Name: " + this.props.modelName);
      loadPart(this.props.partId, this.props.modelName, this.props.partSourceType)
        // downloadFile(modelName)
        .then(buffer => {
          this.setState({ showLoader: false });
          this.props.isLoaded();
          this.clearScene();
          this.showGrid();

          const ext = this.props.modelName.split(".").pop()?.toLowerCase();

          if (ext === "ply") this.loadPLY(buffer);
          else this.loadSTL(buffer);

          this.afterLoad();
        })
        .catch((error) => {
          console.log(`Error loading file '${this.props.modelName}':`, error);
        });
    }
  };

  redraw = () => {
    controls.update();

    const vector = new THREE.Vector3();
    vector.setFromMatrixPosition(mesh.matrixWorld);
    const widthHalf = window.innerWidth / 2;
    const heightHalf = window.innerHeight / 2;

    const labelPosition = vector.project(camera);
    labelPosition.x = labelPosition.x * widthHalf + widthHalf;
    labelPosition.y = -(labelPosition.y * heightHalf) + heightHalf;

    violationLabel.element.style.transform = `translate(-50%, -50%) translate(${labelPosition.x + 200}px,${labelPosition.y + 60}px)`;

    renderer.render(scene, camera);
    labelRenderer.render(scene, camera);
  };

  showGrid() {
    scene.add(grids);
  }

  clearScene = () => {
    while (scene.children.length > 0)
    {
      scene.remove(scene.children[0]);
      scene.children.length = 0;
    }
  };

  loadSTL = (stlData) => {
    const geometry = stlLoader.parse(stlData);
    mesh = newMesh(geometry, material);
    scene.add(mesh);
  };

  loadPLY = (plyData) => {
    let plyObject = plyLoader.parse(plyData);
    if (plyObject.attributes.normal === undefined) {
      plyObject.computeVertexNormals();
    }
    if (plyObject.attributes.color === undefined) {
      mesh = newMesh(plyObject, material);
    } else {
      mesh = newMesh(plyObject, vertMaterial);
    }
    scene.add(mesh);
    scene.background = backgroundColor;
  };

  afterLoad() {
    camera.updateProjectionMatrix();
    camera.add(new THREE.PointLight());
    scene.add(camera);
    scene.add(new THREE.AmbientLight(0xffffff, 0.2));
    let dl = new THREE.DirectionalLight(0xffffff, 0.8);
    dl.position.set(0, -0.5, 1);
    scene.add(dl);
    let hl = new THREE.HemisphereLight(0xffffff, 0, 0.8);
    hl.position.set(0, 0, 1);
    scene.add(hl);
    scene.background = backgroundColor;
    transformControls.attach(mesh);
    scene.add(transformControls as any);
    scene.add(arrowHelper);
    mode = Mode.None;
    this.disableTransformControls();
    arrowHelper.visible = false;
    this.updatePartOrientationValues();
    if (!this.props.results)
      this.snapMesh();
  }
  
  disableTransformControls = () => {
    arrowHelper.visible = false;
    transformControls.enabled = false;
    this.props.setShowCoordinateEdits(false);
    transformControls.showX = transformControls.showY = transformControls.showZ = false;
  }

  toggleMode = (newMode) => {
    this.disableTransformControls();
    if (mode === newMode) {
      mode = Mode.None;
      return false;
    } else {
      mode = newMode;
      return true;
    }
  }

  toggleTranslate = () => {
    if (this.toggleMode(Mode.Translate))
    {
      transformControls.enabled = true;
      this.props.setShowCoordinateEdits(true, mesh);
      transformControls.setMode("translate");
      transformControls.showX = transformControls.showY = true;
      transformControls.showZ = false;
    }
  };

  toggleRotate = () => {
    if (this.toggleMode(Mode.Rotate))
    {
      transformControls.enabled = true;
      this.props.setShowCoordinateEdits(true, mesh);
      transformControls.setMode("rotate");
      transformControls.showX = transformControls.showY = false;
      transformControls.showZ = true;
    }
  };

  toggleScale = () => {
    if (this.toggleMode(Mode.Scale))
    {
      transformControls.enabled = true;
      transformControls.setMode("scale");
      this.props.setShowCoordinateEdits(false, mesh);
      transformControls.showX = transformControls.showY = true;
      transformControls.showZ = false;
    }
  };

  toggleOrient = () => {
    if (this.toggleMode(Mode.Orient))
    {
      transformControls.enabled = true;
      this.props.setShowCoordinateEdits(true, mesh);
      transformControls.setMode("rotate");      
      transformControls.showX = transformControls.showY = transformControls.showZ = true;
    }
  };

  toggleSnap = () => {
    this.toggleMode(Mode.Snap);
    this.snapMesh();
  }

  putMeshOnPlate = () => {
    let bbox = new THREE.Box3().setFromObject(mesh, true);
    const center = new THREE.Vector3();
    bbox.getCenter(center);
    const position = mesh.position;
    mesh.position.set(position.x - center.x, position.y - center.y, position.z - bbox.min.z);
  }

  snapMesh = () => {
    arrowHelper.visible = false;
    this.disableTransformControls();
    //this.putMeshOnPlate();
    this.updatePartOrientationValues();
  }

  updatePartOrientationValues = (updateEdit = true) => {
    this.putMeshOnPlate();
    this.redraw();
    this.materialPosition = mesh.position.clone();
    this.materialRotation = mesh.rotation.clone();
    this.materialScale = mesh.scale.clone();
    if (updateEdit)
      this.props.updateCoordinatesEdit();
    //console.log(rotateAngle);
    //console.log(this.materialPosition);
    //console.log(this.materialRotation);
    //console.log(this.materialScale);
  }
 
  render() {
    return (
      <>
        {this.props.results && <LoadingComponent visible={this.state.showLoader} />}
        <div ref={this.mount}></div>
      </>
    );
  }
}

export default SceneViewer;
